import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Table } from '../types';
import { useData } from './DataContext';

interface TableContextType {
  selectedTable: Table | null;
  selectTable: (tableId: string) => void;
  clearTable: () => void;
}

const TableContext = createContext<TableContextType | undefined>(undefined);

export const useTable = () => {
  const context = useContext(TableContext);
  if (context === undefined) {
    throw new Error('useTable must be used within a TableProvider');
  }
  return context;
};

interface TableProviderProps {
  children: ReactNode;
}

export const TableProvider: React.FC<TableProviderProps> = ({ children }) => {
  // Load selected table ID from localStorage on mount
  const [selectedTableId, setSelectedTableId] = useState<string | null>(() => {
    // First check customer session for table ID
    const customerSession = localStorage.getItem('bookfood_customer_session');
    if (customerSession) {
      try {
        const session = JSON.parse(customerSession);
        if (session.tableId) {
          console.log('🪑 TableContext: Loading selected table from customer session:', session.tableId);
          return session.tableId;
        }
      } catch (error) {
        console.error('Error parsing customer session for table:', error);
      }
    }
    
    // Fallback to standalone table selection
    const saved = localStorage.getItem('bookfood_selectedTableId');
    console.log('🪑 TableContext: Loading selected table from localStorage:', saved);
    return saved;
  });
  const { tables } = useData();

  const selectedTable = selectedTableId 
    ? tables.find(t => t.id === selectedTableId) || null 
    : null;

  // Debug logging for selectedTable
  useEffect(() => {
    console.log('🪑 TableContext: selectedTableId:', selectedTableId);
    console.log('🪑 TableContext: tables count:', tables.length);
    console.log('🪑 TableContext: tables:', tables);
    console.log('🪑 TableContext: selectedTable:', selectedTable);
    if (selectedTableId && !selectedTable) {
      console.warn('⚠️ TableContext: Table ID exists but table not found in tables array!');
    }
  }, [selectedTableId, tables, selectedTable]);

  // Persist selected table ID to localStorage whenever it changes
  useEffect(() => {
    if (selectedTableId) {
      console.log('🪑 TableContext: Saving selected table to localStorage:', selectedTableId);
      localStorage.setItem('bookfood_selectedTableId', selectedTableId);
    } else {
      console.log('🪑 TableContext: Clearing selected table from localStorage');
      localStorage.removeItem('bookfood_selectedTableId');
    }
  }, [selectedTableId]);

  const selectTable = (tableId: string) => {
    console.log('🪑 TableContext: Selecting table:', tableId);
    setSelectedTableId(tableId);
    
    // Also update customer session if it exists
    const customerSession = localStorage.getItem('bookfood_customer_session');
    if (customerSession) {
      try {
        const session = JSON.parse(customerSession);
        session.tableId = tableId;
        localStorage.setItem('bookfood_customer_session', JSON.stringify(session));
        console.log('🪑 TableContext: Updated customer session with table ID');
      } catch (error) {
        console.error('Error updating customer session with table:', error);
      }
    }
  };

  const clearTable = () => {
    console.log('🪑 TableContext: Clearing table selection');
    setSelectedTableId(null);
    
    // Also clear from customer session if it exists
    const customerSession = localStorage.getItem('bookfood_customer_session');
    if (customerSession) {
      try {
        const session = JSON.parse(customerSession);
        delete session.tableId;
        session.loginComplete = false; // Mark as incomplete since table is cleared
        localStorage.setItem('bookfood_customer_session', JSON.stringify(session));
        console.log('🪑 TableContext: Cleared table from customer session');
      } catch (error) {
        console.error('Error clearing table from customer session:', error);
      }
    }
  };

  const value = {
    selectedTable,
    selectTable,
    clearTable
  };

  return (
    <TableContext.Provider value={value}>
      {children}
    </TableContext.Provider>
  );
};
